import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import type { InputChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Input } from "@tql/ui-components-react/kendo/inputs";
import { useState } from "react";
import { flushSync } from "react-dom";

import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import type { RoutingGuideEnumsType } from "~/shared/schemas/routing-guide-schema.ts";

import { getLabelsForSelector } from "../../../modal/utils/get-labels-for-selector.ts";
import type {
	CarrierRule,
	RowUpdate,
	Rule,
} from "../../../modal/utils/types.ts";

export const FieldTextCell = <T extends Rule | CarrierRule>(
	props: GridCustomCellProps & {
		fieldNum: number;
		handleRowChange: (event: RowUpdate<T>) => void;
		isReadOnly: boolean;
		routingGuideEnums?: RoutingGuideEnumsType;
		showActivationError?: boolean;
		cellTestId?: string;
	},
) => {
	const dataItem = props.dataItem as T;
	const { routingGuideEnums } = props;

	const fieldKey = `field${props.fieldNum + 1}` as keyof Rule;
	const ruleItem = dataItem as unknown as Rule;
	const fieldValue = ruleItem[fieldKey];
	const externalValue = (fieldValue as { value: string })?.value ?? "";

	const [localValue, setLocalValue] = useState(externalValue);

	// Reset local value when the row identity, field slot, or field selector changes
	const resetKey = `${dataItem.id}:${props.fieldNum}:${ruleItem.fieldSelector}`;
	const [prevResetKey, setPrevResetKey] = useState(resetKey);
	if (resetKey !== prevResetKey) {
		setPrevResetKey(resetKey);
		setLocalValue(externalValue);
	}

	const labels = getLabelsForSelector(
		ruleItem.fieldSelector ?? "",
		routingGuideEnums,
	);
	const placeholder = labels[props.fieldNum];

	const isDisabled =
		!ruleItem.fieldSelector || !placeholder || props.isReadOnly;

	// For multi-field selectors, only require at least one field to be populated
	const hasAnySiblingValue = labels.some((label, i) => {
		if (!label) return false;
		const siblingKey = `field${i + 1}` as keyof Rule;
		const siblingValue = (ruleItem[siblingKey] as { value: string })?.value;
		return !!siblingValue;
	});

	const hasActivationError =
		props.showActivationError === true &&
		!isDisabled &&
		!localValue &&
		!hasAnySiblingValue;

	const commitValue = (value: string) => {
		// flushSync forces the parent setRules to commit before any subsequent
		// click handler (e.g. "Save and Activate") reads its rules closure,
		// preventing a stale-save defect when the user clicks save while still
		// focused in the input.
		flushSync(() => {
			props.handleRowChange({
				dataItem: dataItem,
				field: `field${props.fieldNum + 1}` as keyof T,
				value: value as T[keyof T],
			});
		});
	};

	return (
		<GridTd data-testid={props.cellTestId} tdProps={props.tdProps}>
			<div
				style={
					hasActivationError
						? {
								outline: "2px solid var(--kendo-color-error)",
								borderRadius: "2px",
							}
						: undefined
				}
			>
				<Input
					disabled={isDisabled}
					onBlur={() => {
						if (localValue !== externalValue) {
							commitValue(localValue);
						}
					}}
					onChange={(e: InputChangeEvent) => {
						setLocalValue(e.value ?? "");
					}}
					placeholder={placeholder}
					type={"text"}
					value={localValue}
				/>
			</div>
			{hasActivationError ? (
				<span
					style={{
						color: "var(--kendo-color-error)",
						display: "block",
						fontSize: "11px",
						marginTop: "2px",
					}}
				>
					A value must be input in order to activate
				</span>
			) : null}
		</GridTd>
	);
};
